/* TEXTY A ČÁSTKY KARTY SMLOUVY — jazyk z appky, měna a zákon ze ZEMĚ TENANTA
 * ============================================================================
 *
 * Majitel 4. 10. 2026 na otázku „Karta smlouvy ve složce klienta je česky
 * i u vietnamského a slovenského poradce, včetně „Kč“. Výpověď se u
 * slovenských klientů píše podle českého zákona. Mám ji přeložit, dát měnu
 * podle země a slovenské znění výpovědi?“ odpověděl „ano“.
 *
 * TŘI VĚCI, KTERÉ SE NESMÍ SLÍT DO JEDNÉ (docs/MENA-A-JAZYK.md):
 *   jazyk  (lang)  = v čem jsou napsané TEXTY karty a editoru — t() z pěti slovníků,
 *   měna   (mena)  = v čem jsou PENÍZE — ze země tenanta (lib/tenantMena.js),
 *   zákon  (zeme)  = podle čí legislativy je VÝPOVĚĎ — taky země tenanta.
 * Vietnamský poradce v Česku: texty vietnamsky, částky v Kč, výpověď česky
 * podle § 2807 NOZ (píše české pojišťovně). Slovenský poradce: texty
 * slovensky, částky v €, výpověď slovensky podle § 800 OZ.
 *
 * PROČ NE ROVNOU useLanguage().t a useFormat():
 *   karta i editor se v testech vykreslují bez LanguageProvideru nebo s mockem,
 *   který nese jen `{ lang }` (druhySmluvSlozka, ContractsCleanList.robustness,
 *   smlouvyStaryBackendAKontrast). Tenhle hook z kontextu vezme jen jazyk
 *   a slovník čte stejným překladačem jako jádro (lib/i18n/textMimoReact) —
 *   stejné slovníky, stejné pořadí hledání (jazyk → čeština → záloha → klíč).
 *   Měnu bere ze stejného úložiště jako LanguageContext (země tenanta), takže
 *   se nemůže rozejít s `currency` ve zbytku appky.
 *
 * PŘEPOČET SE NEDĚLÁ. Smlouva nemá razítko měny — slovenský tenant zadává
 * a vidí eura, český koruny (lib/tenantMena.js, bod 2).
 */
import { useMemo, useSyncExternalStore } from "react";
import * as Jazyk from "@/contexts/LanguageContext";
import { prekladac } from "@/lib/i18n/textMimoReact";
import { jsouParametry } from "@/lib/i18n/pravidla";
import { localeJazyka, plneDatum } from "@/lib/i18n/format";
import { menaZeme, normZeme, odebirejZemi, zemeTenanta } from "@/lib/tenantMena";
import { sJednotkou } from "@/lib/castka";
import { CONTRACT_TYPE_LABEL } from "@/lib/contractMeta";

/* Kontext jazyka, nebo null mimo LanguageProvider (test, izolovaný náhled).
 * useLanguage() vždy nejdřív zavolá useContext, teprve pak případně hodí —
 * pořadí hooků je tedy v každém renderu stejné. */
function useJazykVolitelne() {
  /* Hook vždy bezpodmínečně (rules-of-hooks — podmíněné volání shodilo
   * kompilaci dev serveru 4. 10. 23:19). Chybějící useLanguage (mock v testu)
   * hodí TypeError a skončí v catch → null, stejně jako dřív. */
  try {
    return Jazyk.useLanguage();
  } catch {
    return null;
  }
}

/**
 * Překladač se stejným podpisem jako `t()` z LanguageContextu:
 * `t(klic)`, `t(klic, zaloha)`, `t(klic, { param })`, `t(klic, zaloha, { param })`
 * a `t.plural(zaklad, n)`. (Holý `prekladac` z textMimoReact bere parametry
 * až jako TŘETÍ argument — `t(klic, { x })` by je potichu nedosadil.)
 */
export function prekladacSmluv(lang) {
  const zaklad = prekladac(lang);
  const t = (klic, druhy, treti) => (jsouParametry(druhy)
    ? zaklad(klic, undefined, druhy)
    : zaklad(klic, druhy, treti));
  t.plural = zaklad.plural;
  t.lang = lang;
  return t;
}

/** Neznámé (null / "" / NaN) → null. Nula zůstává nulou — je to údaj. */
export function cisloNeboNic(v) {
  if (v === null || v === undefined || v === "") return null;
  const n = Number(v);
  return Number.isFinite(n) ? n : null;
}

/** Číslo bez měny ve formátu jazyka (oddělovače). Celé číslo bez desetin
 *  („3 500 000“), jinak VŽDY dvě desetiny — u peněz se čeká „42,50 €“,
 *  ne „42,5 €“ (nález ověření 4. 10. 2026). Zaokrouhluje se na centy. */
export function textCisla(v, locale) {
  const n = cisloNeboNic(v);
  if (n === null) return null;
  const desetiny = Math.round(n * 100) % 100 === 0 ? 0 : 2;
  return new Intl.NumberFormat(locale || localeJazyka(), {
    minimumFractionDigits: desetiny,
    maximumFractionDigits: desetiny,
  }).format(n);
}

/** Datum ze smlouvy („2027-03-01“) v pořadí podle jazyka („1. 3. 2027“).
 *  Neznámý tvar (starší data psaná rukou) se ukáže tak, jak je uložený —
 *  nikdy „—“ místo údaje, který poradce zadal. Prázdné → null. */
export function textData(v, lang) {
  if (v === null || v === undefined || v === "") return null;
  return plneDatum(v, lang, { prazdne: String(v) });
}

/** Částka s měnou tenanta („1 500 Kč“, „120,50 €“) — jednotka nezlomitelně. */
export function textCastky(v, { locale, symbol } = {}) {
  const c = textCisla(v, locale);
  return c === null ? null : sJednotkou(c, symbol || menaZeme().symbol);
}

/** Popisek druhu smlouvy v jazyce appky. Neznámý druh = syrový klíč jako dřív
 *  (nikdy klíč slovníku „smlouvaKarta.druh.xyz“). */
export function popisekTypu(t, type) {
  if (!Object.prototype.hasOwnProperty.call(CONTRACT_TYPE_LABEL, type)) return type || "";
  return t(`smlouvaKarta.druh.${type}`, CONTRACT_TYPE_LABEL[type]);
}

/* Příklady do políček editoru podle MĚNY (ne jazyka). Jsou to jen ukázky
 * řádu čísla, žádná data: slovenskému poradci by „např. 3 500 000 €“
 * u hypotéky radilo nesmysl. Neznámá měna → koruny. */
export const PRIKLADY_CASTEK = Object.freeze({
  CZK: Object.freeze({ mesicni: 2500, celkova: 3500000, zbyva: 2400000, nasporeno: 180000, hodnota: 1290000 }),
  EUR: Object.freeze({ mesicni: 100, celkova: 140000, zbyva: 96000, nasporeno: 7200, hodnota: 51600 }),
});
export function prikladyCastek(kodMeny) {
  return PRIKLADY_CASTEK[String(kodMeny || "").toUpperCase()] || PRIKLADY_CASTEK.CZK;
}

/**
 * Vše, co karta, přehled a editor smlouvy potřebují k vykreslení textů a peněz.
 * @returns {{ lang, t, locale, zeme, mena: {code, symbol}, cislo, castka, datum }}
 */
export function useTextySmluv() {
  const ctx = useJazykVolitelne();
  const lang = (ctx && ctx.lang) || "cs";
  const zeme = useSyncExternalStore(odebirejZemi, zemeTenanta, zemeTenanta);
  return useMemo(() => {
    const t = prekladacSmluv(lang);
    const locale = localeJazyka(lang);
    const z = normZeme(zeme);
    const mena = menaZeme(z);
    return {
      lang,
      t,
      locale,
      zeme: z,
      mena,
      cislo: (v) => textCisla(v, locale),
      castka: (v) => textCastky(v, { locale, symbol: mena.symbol }),
      datum: (v) => textData(v, lang),
    };
  }, [lang, zeme]);
}
